import { REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";

const userRegistrationPayloadSchema = z.object({
  keycloakId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  email: z.string().min(1, { message: REQUIRED_MESSAGE }),
  firstName: z.string().min(1, { message: REQUIRED_MESSAGE }),
  lastName: z.string().min(1, { message: REQUIRED_MESSAGE }),
  username: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type UserRegistrationPayloadSchema = z.infer<
  typeof userRegistrationPayloadSchema
>;

interface ProfileData {
  id: string;
  keycloakId: string;
  vendor: string;
  companyCode?: string;
}

interface ProfileQueryKey {
  keycloakId?: string;
  enabled?: boolean;
}

async function getProfileByKeycloakId(
  queryFunctionContext: QueryFunctionContext,
): Promise<ProfileData | null> {
  const { queryKey } = queryFunctionContext;
  const [_, profilesQueryKey] = queryKey;
  const { keycloakId } = profilesQueryKey as ProfileQueryKey;

  if (!keycloakId) {
    throw new Error("keycloakId is required");
  }

  const response = await axiosInstance.get(`/api/auth/profile`);
  return response.data?.data ?? null;
}

function useProfileByKeycloakId(profilesQueryKey: ProfileQueryKey) {
  const { keycloakId, enabled = true } = profilesQueryKey;
  const result = useQuery<ProfileData | null, AxiosError>({
    queryKey: ["profile", { keycloakId }],
    queryFn: getProfileByKeycloakId,
    refetchOnWindowFocus: false,
    enabled: enabled && !!keycloakId,
  });
  return {
    data: result.data,
    error: result.error,
    isSuccess: result.isSuccess,
    isPending: result.isPending,
    isError: result.isError,
  };
}

async function registerUser(
  data: UserRegistrationPayloadSchema,
): Promise<ProfileData> {
  const response = await axiosInstance.post("/api/auth/register", data);
  return response.data;
}

function useRegisterUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: UserRegistrationPayloadSchema }) =>
      registerUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["profile"],
      });
    },
  });
}

export type { ProfileData, UserRegistrationPayloadSchema };
export { useProfileByKeycloakId, useRegisterUser, getProfileByKeycloakId };
